import { Component, OnInit, ViewChild, DestroyRef, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppStateService } from 'src/app/shared/services/helpers/app-state.service';
import * as _ from 'lodash';
import { NgIf, NgClass } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LitmusActionGeneralComponent } from './sub-components/litmus-action-general/litmus-action-general.component';
import { LitmusActionAgeingComponent } from './sub-components/litmus-action-ageing/litmus-action-ageing.component';
import { LitmusActionRulesComponent } from './sub-components/litmus-action-rules/litmus-action-rules.component';
import { LitmusActionSummaryComponent } from './sub-components/litmus-action-summary/litmus-action-summary.component';
import { LitmusActionInboxComponent } from './sub-components/litmus-action-inbox/litmus-action-inbox.component';
import { LitmusActionCategoriesComponent } from './sub-components/litmus-action-categories/litmus-action-categories.component';
import { LitmusActionStatusComponent } from './sub-components/litmus-action-status/litmus-action-status.component';
import { LitmusActionWorkflowsComponent } from './sub-components/litmus-action-workflows/litmus-action-workflows.component';
import { LitmusActionDataFieldsComponent } from './sub-components/litmus-action-data-fields/litmus-action-data-fields.component';
import { CanComponentDeactivate } from 'src/app/guards/can-deactivate.guard';

// Declare Helpers globally (this should be imported from the actual helpers service)
declare const Helpers: any;

@Component({
  selector: 'app-project-action-settings-home',
  templateUrl: './project-action-settings-home.component.html',
  styleUrls: ['./project-action-settings-home.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    NgClass,
    LitmusActionGeneralComponent,
    LitmusActionAgeingComponent,
    LitmusActionRulesComponent,
    LitmusActionSummaryComponent,
    LitmusActionInboxComponent,
    LitmusActionCategoriesComponent,
    LitmusActionStatusComponent,
    LitmusActionWorkflowsComponent,
    LitmusActionDataFieldsComponent,
  ]
})
export class ProjectActionSettingsHomeComponent implements OnInit, CanComponentDeactivate {
  private destroyRef = inject(DestroyRef);
  
  // Tab management
  activeTab: string = 'general';
  // tabList: any = ['general', 'ageing', 'rules', 'summary', 'inbox', 'categories', 'status', 'workflows', 'data-fields'];
  tabList: any = ['general', 'rules', 'summary', 'inbox'];

  // ViewChild references to sub-tab components
  @ViewChild(LitmusActionGeneralComponent) generalComponent?: LitmusActionGeneralComponent;
  @ViewChild(LitmusActionAgeingComponent) ageingComponent?: LitmusActionAgeingComponent;
  @ViewChild(LitmusActionRulesComponent) rulesComponent?: LitmusActionRulesComponent;
  @ViewChild(LitmusActionSummaryComponent) summaryComponent?: LitmusActionSummaryComponent;
  @ViewChild(LitmusActionInboxComponent) inboxComponent?: LitmusActionInboxComponent;
  @ViewChild(LitmusActionCategoriesComponent) categoriesComponent?: LitmusActionCategoriesComponent;
  @ViewChild(LitmusActionStatusComponent) statusComponent?: LitmusActionStatusComponent;
  @ViewChild(LitmusActionWorkflowsComponent) workflowsComponent?: LitmusActionWorkflowsComponent;
  @ViewChild(LitmusActionDataFieldsComponent) dataFieldsComponent?: LitmusActionDataFieldsComponent;

  tabVisblity: any = {
    general: true,
    ageing: false,
    rules: true,
    summary: true,
    inbox: true,
    categories: false,
    status: false,
    workflows: false,
    'data-fields': false
  }

  isUserHasWriteAccess: boolean = false;
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private stateService: AppStateService
  ) { }

  ngOnInit(): void {
    this.isUserHasWriteAccess = this.isUserHasWriteAccessFn()
    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params: any) => {
        let tabName = (this.tabList.indexOf(params['active_tab']) >= 0) ? params['active_tab'] : "general";
        if (!this.tabVisblity[tabName]) tabName = "general";
        // Apply the URL tab only. Navigating here re-emits queryParams and retriggers this subscription.
        if (tabName !== this.activeTab) {
          this.activeTab = tabName;
        }
      });

  }

  /**
   * Toggle between different tabs
   * @param tabName The name of the tab to activate
   */
  async toggleTab(tabName: string): Promise<void> {
    if (tabName === this.activeTab) return;

    // Check if current tab has unsaved changes
    const currentTabComponent = this.getCurrentTabComponent();
    if (currentTabComponent && currentTabComponent.canDeactivate) {
      const canLeave = await currentTabComponent.canDeactivate();
      if (!canLeave) return;
    }

    this.activeTab = tabName;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { active_tab: tabName },
      queryParamsHandling: 'merge'
    });
  }

  /**
   * Get the current active tab component
   */
  private getCurrentTabComponent(): any {
    switch (this.activeTab) {
      case 'general':
        return this.generalComponent;
      case 'ageing':
        return this.ageingComponent;
      case 'rules':
        return this.rulesComponent;
      case 'summary':
        return this.summaryComponent;
      case 'inbox':
        return this.inboxComponent;
      case 'categories':
        return this.categoriesComponent;
      case 'status':
        return this.statusComponent;
      case 'workflows':
        return this.workflowsComponent;
      case 'data-fields':
        return this.dataFieldsComponent;
      default:
        return null;
    }
  }

  /**
   * Can deactivate guard implementation
   */
  canDeactivate(): Promise<boolean> | boolean {
    const currentTabComponent = this.getCurrentTabComponent();
    if (currentTabComponent && currentTabComponent.canDeactivate) {
      return currentTabComponent.canDeactivate();
    }
    return true;
  }

  isUserHasWriteAccessFn(): boolean {
    let isWriteAccess: boolean = false;
    const adminRulesDic = _.cloneDeep(Helpers?.adminRulesDic || {});
    if (adminRulesDic.ACTION_CONFIGURATIONS_SETUP && !_.isEmpty(adminRulesDic.ACTION_CONFIGURATIONS_SETUP) && adminRulesDic.ACTION_CONFIGURATIONS_SETUP.write) {
      isWriteAccess = true;
    }else {
      isWriteAccess = false;
    }
    return isWriteAccess;
  }
}
